import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import React from 'react';

interface EmailTrackingContextValue {
  trackingEnabled: boolean;
  disableTracking: boolean;
  /**
   * Flips to `true` once `initialize()` has been called for the first time
   * in this session. Consumers that mirror `disableTracking` into local
   * state (e.g. EmailActionPreview's editedContent) should defer the first
   * sync until this is `true` so the provider's render-0 default doesn't
   * race with the action's actual value and trigger a phantom autosave.
   */
  isInitialized: boolean;
  setDisableTracking: (value: boolean) => void;
  initialize: (trackingEnabled: boolean, disableTracking: boolean) => void;
}

const EmailTrackingContext = createContext<EmailTrackingContextValue | null>(
  null
);

export function EmailTrackingProvider({ children }: { children: ReactNode }) {
  const [trackingEnabled, setTrackingEnabled] = useState<boolean>(false);
  const [disableTracking, setDisableTracking] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const initializedRef = useRef<boolean>(false);

  const initialize = useCallback(
    (newTrackingEnabled: boolean, newDisableTracking: boolean) => {
      // Always sync trackingEnabled with the current feature-flag value.
      // The flag is `false` while feature flags are loading, then flips to
      // `true` once the API resolves; locking it on first call would
      // permanently suppress the toggle for eligible orgs.
      setTrackingEnabled(newTrackingEnabled);
      // Only seed disableTracking once — the user's in-session toggle
      // change must survive autosave/refetch cycles that re-call initialize.
      if (!initializedRef.current) {
        setDisableTracking(newDisableTracking);
        setIsInitialized(true);
        initializedRef.current = true;
      }
    },
    []
  );

  const handleSetDisableTracking = useCallback((value: boolean) => {
    setDisableTracking(value);
  }, []);

  const contextValue = useMemo(
    () => ({
      trackingEnabled,
      disableTracking,
      isInitialized,
      setDisableTracking: handleSetDisableTracking,
      initialize,
    }),
    [
      trackingEnabled,
      disableTracking,
      isInitialized,
      handleSetDisableTracking,
      initialize,
    ]
  );

  return React.createElement(
    EmailTrackingContext.Provider,
    { value: contextValue },
    children
  );
}

/**
 * Hook to access email tracking state.
 *
 * Must be used within an EmailTrackingProvider.
 * Throws if no provider is found.
 *
 * @returns Context value with trackingEnabled, disableTracking, setDisableTracking, and initialize
 */
export function useEmailTracking(): EmailTrackingContextValue {
  const context = useContext(EmailTrackingContext);
  if (!context) {
    throw new Error(
      'useEmailTracking must be used within an EmailTrackingProvider'
    );
  }
  return context;
}

/**
 * Hook to optionally access email tracking state.
 *
 * Returns null if no provider is found (graceful fallback).
 * Use this in footer components that conditionally render the toggle.
 *
 * @returns Context value or null if no provider
 */
export function useEmailTrackingOptional(): EmailTrackingContextValue | null {
  return useContext(EmailTrackingContext);
}
